import { useState, useEffect, useCallback } from 'react';

function parseJSON<T>(value: string | null, fallback: T): T {
  if (value === null || value === 'undefined') return fallback;
  try {
    return JSON.parse(value) as T;
  } catch {
    return fallback;
  }
}

export default function useLocalStorage<T>(key: string, defaultValue: T): [T, (newValue: T | ((prev: T) => T)) => void] {
  const [value, setValue] = useState<T>(() => {
    if (typeof window === 'undefined') return defaultValue;
    return parseJSON(localStorage.getItem(key), defaultValue);
  });

  useEffect(() => {
    const listener = (e: StorageEvent) => {
      if (e.storageArea === localStorage && e.key === key) {
        setValue(parseJSON(e.newValue, defaultValue));
      }
    };

    window.addEventListener('storage', listener);
    return () => window.removeEventListener('storage', listener);
  }, [key, defaultValue]);

  const setStoredValue = useCallback(
    (newValue: T | ((prev: T) => T)) => {
      setValue((current) => {
        const result = typeof newValue === 'function' ? (newValue as (prev: T) => T)(current) : newValue;
        if (typeof window !== 'undefined') {
          localStorage.setItem(key, JSON.stringify(result));
        }
        return result;
      });
    },
    [key]
  );

  return [value, setStoredValue];
}
